import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import {
  Button,
  Flex,
  Stack,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { ROUTES } from "constants/routes-path";
import { aitBlockService } from "services/aitBlockService";
import { toTablePageableData, getEmptyPageResponse } from "utils";
import { AitBlockFilter } from "./AitBlockFilter";
import SearchTable2 from "components/Tables/SearchTable2";

function AitBlocksList() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(undefined);
  const [searchParams, setSearchParams] = useState({});
  const [response, setResponse] = useState(undefined);
  const [selectedItem, setSelectedItem] = useState(undefined);
  const navigate = useNavigate();
  const dialogRef = useRef(undefined);
  const showToast = useCustomToast();

  const columns = useMemo(() => [
    {
      Header: t("settings.ait-block.list.table.header.applicability"),
      accessor: "aplicabilidade",
    },
    {
      Header: t("settings.ait-block.list.table.header.prefix"),
      accessor: "prefixo",
    },
    {
      Header: t("settings.ait-block.list.table.header.initial-number"),
      accessor: "numero_inicial",
    },
    {
      Header: t("settings.ait-block.list.table.header.end-number"),
      accessor: "numero_final",
    },
    {
      Header: t("settings.ait-block.list.table.header.validator-digit"),
      accessor: "digito_validador",
    },
    {
      Header: t("settings.ait-block.list.table.header.agent"),
      accessor: "id_agente",
    },
    {
      Header: t("settings.ait-block.list.table.header.equipment"),
      accessor: "id_equipamento",
    },
    {
      Header: t("settings.ait-block.list.table.header.creation-date"),
      accessor: "data_criacao",
    },
    {
      Header: t("settings.ait-block.list.table.header.operations"),
      disableSortBy: true,
      actions: [
        {
          label: t('system.actions.edit'),
          permission: 'editar-pessoa',
          onClick: (row) => navigate(ROUTES.aitBlocksEditPath.replace(":id", row.original.id)),
          icon: <FaEdit />
        },
        {
          label: t('system.actions.remove'),
          permission: 'editar-pessoa',
          onClick: ({ original }) => handleRemove(original),
          icon: <FaTrashAlt color="#E53E3E" />,
        }
      ],
    },
  ], []);

  const newAitBlock = () => {
    navigate(ROUTES.aitBlocksAddPath)
  }

  const handleRemove = (row) => {
    setSelectedItem(row);
    dialogRef.current?.displayDialog({
      title: t("settings.ait-block.list.actions.remove-ait-block"),
      message: t("system.actions.remove-confirm"),
    });
  };

  const handleConfirmRemove = async () => {
    try {
      await aitBlockService.delete(selectedItem.id);

      showToast({
        title: t("settings.ait-block.messages.success.delete"),
        status: "success",
      });

      fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  };

  const fetchData = async (params = {}) => {
    if (response) setResponse(undefined);

    try {
      const page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
      params = { ...searchParams, ...params, page: page };
      setSearchParams(params);
      const data = await aitBlockService.list(params);
      setResponse(data);
    } catch (err) {
      setResponse(getEmptyPageResponse(searchParams));
      showToast({ title: t(err), status: "error" });
    }
  };

  const handleFilter = (newFilters) => {
    fetchData({
      filters: newFilters,
    });
  };

  useEffect(() => {
    fetchData()
  }, [])

  const onSearch = (term) => {
    setSearchTerm(term);
    const payload = {
      page: defaultPageable.page,
      pageSize: searchParams?.pageSize || defaultPageable.pageSize,
      termo: term
    }
    fetchData(payload);
  }

  return (
    <Flex gap="5" direction="column" pt={{ base: "150px", lg: "90px" }}>
      <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
        <Button
          variant="list-primary"
          leftIcon={<AddIcon fontSize="sm" />}
          alignSelf={{ sm: "flex-start", lg: undefined }}
          onClick={newAitBlock}
        >
          {t('settings.ait-block.list.actions.new-ait-block')}
        </Button>

        <Stack
          direction='row'
          spacing='10px'
          alignSelf={{ sm: 'flex-start', lg: 'auto' }}
          pt={{ sm: "20px", md: "0",  lg: "0" }}
        >
          <AitBlockFilter onFilter={handleFilter} />
        </Stack>
      </Flex>

      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader title={t("settings.ait-block.title")} subtitle={t("settings.ait-block.description")} />

        <CardBody>
          {response ? (
            <SearchTable2
              columnsData={columns}
              tableData={response.content}
              pageableData={toTablePageableData(response, { defaultField: "id", ...searchParams })}
              searchTerm={searchTerm}
              setSearchTerm={onSearch}
              onChange={fetchData}
            />
          ) : (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          )}
        </CardBody>
      </Card>
      <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
    </Flex>
  );
}

export default AitBlocksList;